<template>
  <div class="modal fade" id="savedFilterModal" tabindex="-1" aria-labelledby="savedFilterModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <!-- Step 1: Choose action (create new or update existing) -->
        <div v-if="step === 1 && savedFilters.length > 0 && !showAuthForm" class="step step--1 px-3">
          <div class="modal-header py-4 px-3 justify-content-center">
            <div class="text-center">
              <div class="h1 modal-title mb-2" id="savedFilterModalLabel">
                {{ props.translations.header }}
              </div>
            </div>
            <button type="button" @click="resetModalState" class="btn-close" data-bs-dismiss="modal" aria-label="Close">
              <img class="icon icon--close" src="/images/mg/icons/ico-close.svg" alt="">
            </button>
          </div>

          <div class="modal-body pt-0 pb-4">
            <fieldset>
              <p class="text--xsmall text-center text--grey mb-3">
                {{ props.translations.selectAction }}
              </p>

              <div class="mb-4 pb-2">
                <div class="form-check">
                  <input class="form-check-input" type="radio" v-model="savedFilterAction" value="create" id="savedCarFilter-action__create" checked>
                  <label class="form-check-label" for="savedCarFilter-action__create">
                    {{ props.translations.actions.saveNew }}
                  </label>
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" v-model="savedFilterAction" value="update" id="savedCarFilter-action__update">
                  <label class="form-check-label" for="savedCarFilter-action__update">
                    {{ props.translations.actions.updateExisting }}
                  </label>
                </div>
              </div>

              <div v-if="savedFilterAction === 'update'" class="mb-4 pb-2">
                <div class="form-group mb-0">
                  <label for="savedCarFilter-savedFilterId" class="form-label visually-hidden">{{ props.translations.chooseFilter }}</label>
                  <select
                    v-model="savedFilterId"
                    id="savedCarFilter-savedFilterId"
                    class="form-select"
                    :class="{ 'parsley-error': errors.savedFilterId }"
                  >
                    <option value="">{{ props.translations.chooseFilter }}</option>
                    <option v-for="filter in savedFilters" :key="filter.id" :value="filter.id">{{ filter.name }}</option>
                  </select>
                  <p v-if="errors.savedFilterId" class="text-danger">
                    {{ errors.savedFilterId }}
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="btn btn-next btn-arrow btn-primary btn-lg borderRadius w-100"
                @click="nextStep"
              >
                {{ props.translations.next }}
                <img src="/images/icons/ico-arrow-circle-white.svg" alt="" class="icon icon--arrow-circle">
              </button>
            </fieldset>
          </div>
        </div>

        <!-- Step 2: Create new filter -->
        <div v-if="((step === 2 && savedFilterAction === 'create') || (step === 1 && savedFilters.length === 0)) && !showAuthForm" class="step step--create px-3">
          <div class="modal-header py-4 px-3 justify-content-center">
            <div class="text-center">
              <div class="h1 modal-title mb-2" id="savedFilterModalLabel">
                {{ props.translations.header }}
              </div>
            </div>
            <button type="button" @click="resetModalState" class="btn-close" data-bs-dismiss="modal" aria-label="Close">
              <img class="icon icon--close" src="/images/mg/icons/ico-close.svg" alt="">
            </button>
          </div>

          <div class="modal-body pt-0 pb-4">
            <fieldset>
              <p class="text--xsmall text-center text--grey mb-3">
                {{ props.translations.setFilterName }}
              </p>

              <div class="mb-4 pb-2">
                <div class="form-group mb-0">
                  <label for="savedCarFilter-name" class="form-label visually-hidden">
                    {{ props.translations.validationAttributes.savedFilterName }}
                  </label>
                  <input
                    v-model="savedFilterName"
                    id="savedCarFilter-name"
                    class="form-control form-control--lg"
                    :class="{ 'parsley-error': errors.savedFilterName }"
                    :placeholder="props.translations.setFilterNamePlaceholder"
                  >
                  <p v-if="errors.savedFilterName" class="text-danger">
                    {{ errors.savedFilterName }}
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="btn btn-next btn-arrow btn-primary btn-lg borderRadius w-100"
                @click="nextStep"
                :disabled="!savedFilterName"
              >
                {{ props.translations.next }}
                <img src="/images/icons/ico-arrow-circle-white.svg" alt="" class="icon icon--arrow-circle">
              </button>
            </fieldset>
          </div>
        </div>

        <!-- Step 3: Set notification mode -->
        <div v-if="step === 3 && !showAuthForm && !finished" class="step step--3 px-3">
          <div class="modal-header py-4 px-3 justify-content-center">
            <div class="text-center">
              <div class="h1 modal-title mb-2" id="savedFilterModalLabel">
                {{ props.translations.header }}
              </div>
            </div>
            <button type="button" @click="resetModalState" class="btn-close" data-bs-dismiss="modal" aria-label="Close">
              <img class="icon icon--close" src="/images/mg/icons/ico-close.svg" alt="">
            </button>
          </div>

          <div class="modal-body pt-0 pb-4">
            <fieldset>
              <div class="mb-2">
                <p class="text--xsmall text-center text--grey mb-3">
                  {{ props.translations.selectNotificationMode }}
                </p>

                <div class="mb-4 pb-2">
                  <div class="form-check">
                    <input class="form-check-input" type="radio" v-model="notificationMode" value="daily" id="savedCarFilter-notificationMode__daily">
                    <label class="form-check-label" for="savedCarFilter-notificationMode__daily" v-html="props.translations.notificationModes.daily"></label>
                  </div>

                  <div class="form-check">
                    <input class="form-check-input" type="radio" v-model="notificationMode" value="instant" id="savedCarFilter-notificationMode__instant">
                    <label class="form-check-label" for="savedCarFilter-notificationMode__instant" v-html="props.translations.notificationModes.instant"></label>
                  </div>

                  <div class="form-check">
                    <input class="form-check-input" type="radio" v-model="notificationMode" value="disabled" id="savedCarFilter-notificationMode__disabled">
                    <label class="form-check-label" for="savedCarFilter-notificationMode__disabled" v-html="props.translations.notificationModes.disabled"></label>
                  </div>
                </div>
                <p v-if="errors.notificationMode" class="text-danger">
                  {{ errors.notificationMode }}
                </p>

                <div v-if="Object.keys(errors).length > 0" class="alert-box alert-danger">
                  <p v-for="(error, index) in Object.values(errors)" :key="index">{{ error }}</p>
                </div>

                <button type="submit" @click.prevent="submit" class="btn btn-arrow btn-primary btn-lg borderRadius w-100">
                  {{ props.translations.submitAuth }}
                  <img src="/images/icons/ico-arrow-circle-white.svg" alt="" class="icon icon--arrow-circle">
                </button>
              </div>
            </fieldset>
          </div>
        </div>

        <!-- Authentication step -->
        <div v-if="showAuthForm" class="step step--auth px-3">
          <div class="modal-header py-4 px-3 justify-content-center">
            <div class="text-center">
              <div class="h1 modal-title mb-2" id="savedFilterModalLabel">
                {{ props.translations.auth.title }}
              </div>
            </div>
            <button type="button" @click="resetModalState" class="btn-close" data-bs-dismiss="modal" aria-label="Close">
              <img class="icon icon--close" src="/images/mg/icons/ico-close.svg" alt="">
            </button>
          </div>

          <div class="modal-body pt-0 pb-4">
            <div class="d-flex justify-content-center mb-3 pb-3">
              <ul class="nav nav-tabs nav-tabs--login" id="tabsLoginFilter" role="tablist">
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    :class="{ 'active': activeAuthTab === 'login' }"
                    @click="activeAuthTab = 'login'"
                    type="button"
                  >
                    {{ props.translations.auth.login.tab }}
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    :class="{ 'active': activeAuthTab === 'register' }"
                    @click="activeAuthTab = 'register'"
                    type="button"
                  >
                    {{ props.translations.auth.register.tab }}
                  </button>
                </li>
              </ul>
            </div>

            <div class="tab-content">
              <!-- Login Tab -->
              <div
                class="tab-pane fade"
                :class="{ 'show active': activeAuthTab === 'login' }"
              >
                <fieldset>
                  <div class="mb-2">
                    <p class="text--xsmall text-center text--grey mb-3">
                      {{ props.translations.auth.login.info }}
                    </p>

                    <div class="mb-4 pb-2">
                      <div class="mb-2">
                        <label for="formLoginEmailFilter" class="form-label visually-hidden">{{ props.translations.auth.login.email }}</label>
                        <input
                          v-model="loginEmail"
                          type="email"
                          class="form-control form-control--lg"
                          :class="{ 'parsley-error': errors.loginEmail }"
                          id="formLoginEmailFilter"
                          :placeholder="props.translations.auth.login.email"
                          required
                        >
                        <p v-if="errors.loginEmail" class="text-danger">
                          {{ errors.loginEmail }}
                        </p>
                      </div>

                      <div class="input-group mb-3">
                        <label for="formLoginPassFilter" class="form-label visually-hidden">{{ props.translations.auth.login.password }}</label>
                        <input
                          v-model="loginPassword"
                          :type="loginPasswordFieldType"
                          class="form-control form-control--lg"
                          :class="{ 'parsley-error': errors.loginPassword }"
                          id="formLoginPassFilter"
                          :placeholder="props.translations.auth.login.password"
                          required
                        >
                        <span
                          class="input-group-text input-group-text--eye togglePassword"
                          :class="{ 'parsley-error': errors.loginPassword }"
                          role="button"
                          tabindex="0"
                          @click="togglePasswordVisibility('login')"
                        >
                          <img class="icon icon--w24" :src="getPasswordToggleIcon(loginPasswordFieldType)" alt="">
                        </span>
                      </div>
                      <p v-if="errors.loginPassword" class="text-danger">
                        {{ errors.loginPassword }}
                      </p>

                      <div class="text-end mb-3 pb-3">
                        <a href="#" class="link link-primary text--semi-small">
                          {{ props.translations.auth.login.forgotPassword }}
                        </a>
                      </div>

                      <button @click="login" type="button" class="btn btn-primary btn-login w-100">
                        {{ props.translations.auth.login.submit }}
                        <img src="/images/icons/ico-login-white.svg" alt="" class="icon icon--login icon--w24">
                      </button>
                    </div>
                  </div>
                </fieldset>
              </div>

              <!-- Register Tab -->
              <div
                class="tab-pane fade"
                :class="{ 'show active': activeAuthTab === 'register' }"
              >
                <fieldset>
                  <div class="mb-2">
                    <p class="text--xsmall text-center text--grey mb-3">
                      {{ props.translations.authenticationRequired }}
                    </p>

                    <div class="mb-4 pb-2">
                      <div class="mb-2">
                        <label for="savedCarFilter-registrationEmail" class="form-label visually-hidden">{{ props.translations.auth.register.email }}</label>
                        <input
                          v-model="registrationEmail"
                          type="email"
                          class="form-control form-control--lg"
                          :class="{ 'parsley-error': errors.registrationEmail }"
                          id="savedCarFilter-registrationEmail"
                          :placeholder="props.translations.auth.register.email"
                          required
                        >
                        <p v-if="errors.registrationEmail" class="text-danger">
                          {{ errors.registrationEmail }}
                        </p>
                      </div>

                      <div class="live-password-validation">
                        <div class="input-group mb-2">
                          <label for="savedCarFilter-registrationPassword" class="form-label visually-hidden">{{ props.translations.auth.register.password }}</label>
                          <input
                            v-model="registrationPassword"
                            :type="registrationPasswordFieldType"
                            class="form-control form-control--lg"
                            :class="{ 'parsley-error': errors.registrationPassword }"
                            id="savedCarFilter-registrationPassword"
                            :placeholder="props.translations.auth.register.password"
                            required
                          >
                          <span
                            class="input-group-text input-group-text--eye togglePassword"
                            :class="{ 'parsley-error': errors.registrationPassword }"
                            role="button"
                            tabindex="0"
                            @click="togglePasswordVisibility('register')"
                          >
                            <img class="icon icon--w24" :src="getPasswordToggleIcon(registrationPasswordFieldType)" alt="">
                          </span>
                        </div>
                        <p v-if="errors.registrationPassword" class="text-danger">
                          {{ errors.registrationPassword }}
                        </p>

                        <ul class="list list-unstyled mb-2">
                          <li>
                            <small
                              class="form-validation password-length"
                              :class="passwordLengthValidationClass"
                            >
                              {{ props.translations.auth.register.passwordRules.length }}
                            </small>
                          </li>
                          <li>
                            <small
                              class="form-validation password-mixed-case"
                              :class="passwordMixedCaseValidationClass"
                            >
                              {{ props.translations.auth.register.passwordRules.mixedCase }}
                            </small>
                          </li>
                        </ul>
                      </div>

                      <div class="input-group mb-0">
                        <label for="savedCarFilter-registrationPasswordConfirmation" class="form-label visually-hidden">{{ props.translations.auth.register.passwordConfirmation }}</label>
                        <input
                          v-model="registrationPasswordConfirmation"
                          :type="registrationPasswordConfirmationFieldType"
                          class="form-control form-control--lg"
                          :class="{ 'parsley-error': errors.registrationPasswordConfirmation }"
                          id="savedCarFilter-registrationPasswordConfirmation"
                          :placeholder="props.translations.auth.register.passwordConfirmation"
                          required
                        >
                        <span
                          class="input-group-text input-group-text--eye togglePassword"
                          :class="{ 'parsley-error': errors.registrationPasswordConfirmation }"
                          role="button"
                          tabindex="0"
                          @click="togglePasswordVisibility('registerConfirmation')"
                        >
                          <img class="icon icon--w24" :src="getPasswordToggleIcon(registrationPasswordConfirmationFieldType)" alt="">
                        </span>
                      </div>
                      <p v-if="errors.registrationPasswordConfirmation" class="text-danger">
                        {{ errors.registrationPasswordConfirmation }}
                      </p>
                    </div>

                    <button @click="register" type="button" class="btn btn-login btn-primary btn-lg borderRadius w-100">
                      {{ props.translations.auth.register.submit }}
                      <img src="/images/icons/ico-newsletter-white.svg" alt="" class="icon icon--login icon--w24">
                    </button>

                    <div class="text-center mt-4">
                      <small class="text--grey" v-html="props.translations.auth.register.consent"></small>
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
        </div>

        <!-- Success step -->
        <div v-if="finished" class="step step--finished px-3">
          <div class="modal-header py-4 px-3 justify-content-center">
            <div class="text-center">
              <div class="h1 modal-title mb-2" id="savedFilterModalLabel">
                {{ props.translations.header }}
              </div>
            </div>
            <button type="button" @click="resetModalState" class="btn-close" data-bs-dismiss="modal" aria-label="Close">
              <img class="icon icon--close" src="/images/mg/icons/ico-close.svg" alt="">
            </button>
          </div>

          <div class="modal-body pt-0 pb-4">
            <div class="text-center">
              <div class="mb-4">
<!--                <img src="/images/icons/ico-check.svg" alt="" class="icon icon&#45;&#45;w64">-->
              </div>
              <h2 class="h3 mb-3">{{ props.translations.finishedTitle }}</h2>
              <p class="mb-4">
                {{ props.translations.finishedText }}
                <br>
                ({{ finishedSavedCarFilterName }})
              </p>
              <button type="button" class="btn btn-primary btn-lg w-100" data-bs-dismiss="modal" @click="resetModalState">
                {{ props.translations.finishedButton }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useSaveCarFilterModal } from '@/composables/useSaveCarFilterModal'

// Props
const props = defineProps({
  currentFilterSettings: {
    type: Object,
    required: true
  },
  currentFilterResults: {
    type: Array,
    required: true
  },
  translations: {
    type: Object,
    required: true
  }
})

// Use the composable for all logic
const {
  // State
  step,
  savedFilterAction,
  savedFilterName,
  savedFilterId,
  notificationMode,
  errors,
  savedFilters,
  finished,
  finishedSavedCarFilterName,
  isAuthenticated,
  showAuthForm,
  loginEmail,
  loginPassword,
  registrationEmail,
  registrationPassword,
  registrationPasswordConfirmation,
  activeAuthTab,
  shouldReloadAfterClose,

  // Methods
  resetModalState,
  nextStep,
  submit,
  checkAuthStatus,
  login,
  register
} = useSaveCarFilterModal(props)

const loginPasswordFieldType = ref('password')
const registrationPasswordFieldType = ref('password')
const registrationPasswordConfirmationFieldType = ref('password')

const passwordLengthValidationClass = computed(() => {
  if (registrationPassword.value.length === 0) {
    return ''
  }

  return registrationPassword.value.length >= 8 ? 'success' : 'invalid'
})

const passwordMixedCaseValidationClass = computed(() => {
  if (registrationPassword.value.length === 0) {
    return ''
  }

  const hasLowercaseLetter = /[a-z]/.test(registrationPassword.value)
  const hasUppercaseLetter = /[A-Z]/.test(registrationPassword.value)

  return hasLowercaseLetter && hasUppercaseLetter ? 'success' : 'invalid'
})

function togglePasswordVisibility(field) {
  if (field === 'login') {
    loginPasswordFieldType.value = loginPasswordFieldType.value === 'password' ? 'text' : 'password'
    return
  }

  if (field === 'register') {
    registrationPasswordFieldType.value = registrationPasswordFieldType.value === 'password' ? 'text' : 'password'
    return
  }

  registrationPasswordConfirmationFieldType.value = registrationPasswordConfirmationFieldType.value === 'password' ? 'text' : 'password'
}

function getPasswordToggleIcon(fieldType) {
  return fieldType === 'password' ? '/images/icons/ico-eye-grey.svg' : '/images/icons/ico-eye-off-grey.svg'
}

watch(showAuthForm, (isVisible) => {
  if (!isVisible) {
    loginPasswordFieldType.value = 'password'
    registrationPasswordFieldType.value = 'password'
    registrationPasswordConfirmationFieldType.value = 'password'
  }
})
</script>
